<template>
  <div v-loading="loading">
    <el-row class="statLinkDiv">
      <el-button
        type="primary"
        round
        :plain="period !== 'week'"
        @click="loadWeek"
      >Week</el-button>
      <el-button
        type="primary"
        round
        :plain="period !== 'lastDay'"
        @click="loadLastDay"
      >24 hours</el-button>
      <el-button
        type="primary"
        round
        :plain="period !== 'today'"
        @click="loadToday"
      >Today</el-button>
    </el-row>

    <el-tabs v-model="statTab">
      <el-tab-pane label="Tasks" name="tasks">
        <el-row>
          <line-chart
            :options="getLineOptions()"
            :data="getChart()"
            :discrete="true"
          />
        </el-row>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'BotStats',
  props: {
    id: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      statTab: 'tasks',
      period: 'today',
      points: [],
      loading: false,
      requestId: 0
    }
  },
  mounted () {
    this.loadToday()
  },
  methods: {
    loadWeek () {
      const to = moment().startOf('day')
      const from = to.clone().subtract(6, 'days')

      return this.loadPeriod('week', () => {
        return this.requestDaily(from, to)
          .then(items => this.makeDailyPoints(items, from, 7))
      })
    },
    loadLastDay () {
      const to = moment().startOf('hour')
      const from = to.clone().subtract(23, 'hours')
      const buckets = this.makeBuckets(from, 24, 'hours')
      const dates = this.uniqueDates(buckets)

      return this.loadPeriod('lastDay', () => {
        return Promise.all(dates.map(date => this.requestHourly(date)))
          .then(results => this.makeHourlyPoints(
            [].concat(...results),
            buckets,
            'HH:00'
          ))
      })
    },
    loadToday () {
      const now = moment()
      const from = now.clone().startOf('day')
      const buckets = this.makeBuckets(from, now.hour() + 1, 'hours')

      return this.loadPeriod('today', () => {
        return this.requestHourly(from)
          .then(items => this.makeHourlyPoints(items, buckets, 'HH:00'))
      })
    },
    loadPeriod (period, loader) {
      const requestId = ++this.requestId
      this.period = period
      this.loading = true

      return loader()
        .then(points => {
          if (requestId === this.requestId) {
            this.points = points
          }
        })
        .catch(() => {
          if (requestId === this.requestId) {
            this.points = []
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false
          }
        })
    },
    requestHourly (date) {
      return this.$store.dispatch('project/loadReport', {
        project_id: this.$route.params.id,
        bot_id: this.id,
        date: moment(date).format('YYYY-MM-DD')
      }).then(response => response.data || [])
    },
    requestDaily (from, to) {
      return this.$store.dispatch('project/loadDayReport', {
        project_id: this.$route.params.id,
        bot_id: this.id,
        from_date: moment(from).format('YYYY-MM-DD'),
        to_date: moment(to).format('YYYY-MM-DD')
      }).then(response => response.data || [])
    },
    makeBuckets (from, count, unit) {
      const buckets = []
      for (let index = 0; index < count; index++) {
        buckets.push(from.clone().add(index, unit))
      }
      return buckets
    },
    uniqueDates (buckets) {
      return buckets.reduce((dates, bucket) => {
        const date = bucket.format('YYYY-MM-DD')
        if (!dates.includes(date)) {
          dates.push(date)
        }
        return dates
      }, [])
    },
    makeHourlyPoints (items, buckets, labelFormat) {
      const itemsByHour = items.reduce((result, item) => {
        if (item.id && typeof item.id === 'object') {
          const key = moment({
            year: item.id.year,
            month: item.id.month - 1,
            day: item.id.day,
            hour: item.id.hour
          }).format('YYYY-MM-DD HH')
          result[key] = item
        }
        return result
      }, {})

      return buckets.map(bucket => {
        return this.makePoint(
          bucket.format(labelFormat),
          itemsByHour[bucket.format('YYYY-MM-DD HH')]
        )
      })
    },
    makeDailyPoints (items, from, count) {
      const itemsByDay = items.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})

      return this.makeBuckets(from, count, 'days').map(day => {
        return this.makePoint(
          day.format('DD.MM'),
          itemsByDay[day.format('YYYY-MM-DD')]
        )
      })
    },
    makePoint (label, item = {}) {
      return {
        label: label,
        success: Number(item.success) || 0,
        errors: Number(item.errors) || 0,
        total: Number(item.total) || 0
      }
    },
    getChart () {
      return [
        { name: 'Success', data: this.getSeries('success') },
        { name: 'Errors', data: this.getSeries('errors') },
        { name: 'Total', data: this.getSeries('total') }
      ]
    },
    getSeries (field) {
      return this.points.reduce((result, point) => {
        result[point.label] = point[field]
        return result
      }, {})
    },
    getLineOptions () {
      return {
        scales: {
          xAxes: [{ stacked: false }],
          yAxes: [{
            stacked: false,
            ticks: { beginAtZero: true }
          }]
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.statLinkDiv {
  text-align: center;
  cursor: pointer;
}
</style>
